import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Hand, MousePointer2, Pause, Play, X } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useSettings } from "@/engine/settingsStore";
import { DRAG_MS, POINTER_MOVE_MS, WALKTHROUGH_SCENES, pointerTourMs, sceneDuration } from "@/components/tutorial/walkthrough-scenes";

// There is no score: the walkthrough explains that choices shape a life rather than awarding points.
import { playWalkthroughCue, stopWalkthroughCues } from "@/components/tutorial/walkthrough-sound";
import {
  TUTORIAL_FINISH_EVENT,
  TUTORIAL_HOME_VIEW_EVENT,
  TUTORIAL_NEWS_SAMPLE_EVENT,
  TUTORIAL_START_EVENT,
  TUTORIAL_TARGET_EVENT,
  setTutorialActive,
  type TutorialHomeView,
} from "@/components/tutorial/tutorial-session";

export const TUTORIAL_DONE_KEY = "coe-tutorial-completed";
export const TUTORIAL_REOPEN_EVENT = TUTORIAL_START_EVENT;
const CARD_GAP = 14;
/** Autoplay waits for the spotlight target to be visible, but never longer than this. */
const TARGET_WAIT_MS = 4000;

type Placement = { left: number; top: number; side: "right" | "left" | "top" | "bottom" | "dock" };

/** Places the card beside the target without overlapping it; docks to the far edge when no side fits.
 *  On a phone the card is a sheet docked at the bottom edge (the top edge only
 *  when the bottom would hide more of the target), so it does not hop about
 *  from step to step. `prefer` keeps the previous side while it still fits. */
export function placeCard(t: { left: number; top: number; width: number; height: number } | null, cw: number, ch: number, vw: number, vh: number, mobile: boolean, prefer?: Placement["side"]): Placement {
  const m = 12;
  const clampX = (x: number) => Math.max(m, Math.min(vw - cw - m, x));
  const clampY = (y: number) => Math.max(m, Math.min(vh - ch - m, y));
  if (!t) return { left: clampX(vw - cw - m), top: clampY(vh - ch - m), side: "dock" };
  const cy = t.top + t.height / 2, cx = t.left + t.width / 2;
  const hidden = (top: number) => Math.max(0, Math.min(top + ch, t.top + t.height, vh) - Math.max(top, t.top, 0));
  if (mobile) {
    // The top berth sits just under the page header, so the header's own
    // buttons (which several steps press) stay in view either way. The sheet
    // stays on the edge it is on unless that edge would hide a good part of
    // what it explains, so it does not swing up and down from step to step.
    const bottom = vh - ch - m, top = Math.min(MOBILE_TOP_BERTH, Math.max(m, vh - ch - m));
    const at = prefer === "top" ? top : bottom, other = at === top ? bottom : top;
    const allowance = Math.min(t.height, vh) * 0.15;
    return { left: m, top: hidden(at) <= allowance || hidden(at) <= hidden(other) ? at : other, side: "dock" };
  }
  const tries: Placement[] = [
    { left: t.left + t.width + CARD_GAP, top: clampY(cy - ch / 2), side: "right" },
    { left: t.left - cw - CARD_GAP, top: clampY(cy - ch / 2), side: "left" },
    { left: clampX(cx - cw / 2), top: t.top + t.height + CARD_GAP, side: "bottom" },
    { left: clampX(cx - cw / 2), top: t.top - ch - CARD_GAP, side: "top" },
  ];
  if (prefer) tries.sort((a, b) => (b.side === prefer ? 1 : 0) - (a.side === prefer ? 1 : 0));
  for (const p of tries) {
    if (p.left >= m && p.top >= m && p.left + cw <= vw - m && p.top + ch <= vh - m) return p;
  }
  // No side fits: dock at an edge of the screen, whichever hides the least of
  // the target. The top and bottom edges come first (a tall target is
  // scrolled to the top, so its start stays in view and the card covers its
  // tail; ties go to the bottom); on a short screen, a phone held sideways,
  // the left or right edge may hide less, and some of the highlight must
  // stay visible to be pressed.
  const covered = (p: { left: number; top: number }) => {
    const w = Math.max(0, Math.min(p.left + cw, t.left + t.width, vw) - Math.max(p.left, t.left, 0));
    const h = Math.max(0, Math.min(p.top + ch, t.top + t.height, vh) - Math.max(p.top, t.top, 0));
    return w * h;
  };
  const docks: Placement[] = [
    { left: clampX(cx > vw / 2 ? m : vw - cw - m), top: vh - ch - m, side: "dock" },
    { left: clampX(cx > vw / 2 ? m : vw - cw - m), top: m, side: "dock" },
    { left: m, top: clampY(cy - ch / 2), side: "dock" },
    { left: vw - cw - m, top: clampY(cy - ch / 2), side: "dock" },
  ];
  // Top or bottom by how much of the target's height they hide, as before;
  // the side edges only when they hide clearly less of its area.
  const vertical = hidden(vh - ch - m) <= hidden(m) ? docks[0] : docks[1];
  const side = covered(docks[2]) <= covered(docks[3]) ? docks[2] : docks[3];
  return covered(side) < covered(vertical) * 0.8 ? side : vertical;
}

/** One motion for everything that travels (spotlight, card): a soft spring
 *  that glides across the page between steps yet keeps up with a page that
 *  is scrolling, so nothing lags behind and then snaps. */
const GLIDE = { type: "spring" as const, stiffness: 170, damping: 26, mass: 1 };
/** A target that vanishes for a moment (a panel re-rendering, the next
 *  step's control not drawn yet) keeps its spotlight this long, so the
 *  spotlight glides on instead of blinking out and back. */
const TARGET_GRACE_MS = 450;
/** How long a target must hold still before the card is placed around it. */
const SETTLE_MS = 160;
/** The longest the card waits for a moving target before it is placed anyway. */
const MAX_HOLD_MS = 1500;

/** The spotlight is drawn this far outside the box it lights. */
const SPOTLIGHT_PAD = 6;
/** Is a point inside the lit section, padding included? */
export function inHighlight(box: { left: number; top: number; width: number; height: number }, x: number, y: number): boolean {
  return x >= box.left - SPOTLIGHT_PAD && x <= box.left + box.width + SPOTLIGHT_PAD
    && y >= box.top - SPOTLIGHT_PAD && y <= box.top + box.height + SPOTLIGHT_PAD;
}

const overlapsBox = (a: TargetBox, p: { left: number; top: number }, w: number, h: number, gap = 6) =>
  p.left < a.left + a.width + gap && p.left + w > a.left - gap && p.top < a.top + a.height + gap && p.top + h > a.top - gap;
/** Where a phone's top-docked card starts: below the sticky page header. */
const MOBILE_TOP_BERTH = 60;
const AUTO_PRESS_MS = 1800;
const PRESS_ANIMATION_MS = 450;

export function hasCompletedTutorial(): boolean {
  if (typeof window === "undefined") return true;
  try { return localStorage.getItem(TUTORIAL_DONE_KEY) === "1"; }
  catch { return true; }
}

/** Opens the route-aware walkthrough without clearing first-visit completion. */
export function resetTutorial(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(TUTORIAL_START_EVENT, { detail: { replay: true } }));
}

type TargetBox = { left: number; top: number; width: number; height: number };

/** The nearest ancestor that scrolls vertically (a panel's list, a sheet). */
function scrollParent(el: HTMLElement): HTMLElement | null {
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const oy = getComputedStyle(node).overflowY;
    if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight + 1) return node;
  }
  return null;
}

/** The part of an element that can actually be seen: its box clipped by every
 *  ancestor that clips its content, and by the window. A list inside a short
 *  scrolling panel is spotlighted where the panel shows it, not where its
 *  hidden rows would be. */
function visibleBox(el: HTMLElement): TargetBox | null {
  const r = el.getBoundingClientRect();
  let left = r.left, top = r.top, right = r.right, bottom = r.bottom;
  for (let node = el.parentElement; node && node !== document.body; node = node.parentElement) {
    const s = getComputedStyle(node);
    if (s.overflowX === "visible" && s.overflowY === "visible") continue;
    const c = node.getBoundingClientRect();
    if (s.overflowY !== "visible") { top = Math.max(top, c.top); bottom = Math.min(bottom, c.bottom); }
    if (s.overflowX !== "visible") { left = Math.max(left, c.left); right = Math.min(right, c.right); }
  }
  left = Math.max(left, 0); top = Math.max(top, 0);
  right = Math.min(right, window.innerWidth); bottom = Math.min(bottom, window.innerHeight);
  return right - left > 1 && bottom - top > 1 ? { left, top, width: right - left, height: bottom - top } : null;
}

/** "a+b" spotlights the union of several elements; "a,b" = first visible wins. */
function findBox(ids: string): TargetBox | null {
  const boxes = ids.split("+").map((part) => { const el = findTarget(part); return el ? visibleBox(el) : null; })
    .filter((r): r is TargetBox => !!r)
    .map((b) => ({ left: b.left, top: b.top, right: b.left + b.width, bottom: b.top + b.height }));
  if (!boxes.length) return null;
  const left = Math.min(...boxes.map((b) => b.left)), top = Math.min(...boxes.map((b) => b.top));
  const right = Math.max(...boxes.map((b) => b.right)), bottom = Math.max(...boxes.map((b) => b.bottom));
  return { left, top, width: right - left, height: bottom - top };
}

function isFullyShown(el: HTMLElement): boolean {
  let opacity = 1;
  for (let node: HTMLElement | null = el; node && opacity > 0.9; node = node.parentElement) {
    opacity *= parseFloat(getComputedStyle(node).opacity || "1");
  }
  return opacity > 0.9;
}

/** Click an element the way a player would; SVG nodes have no .click(). */
function clickElement(el: Element | null | undefined) {
  if (!el) return;
  if (typeof (el as HTMLElement).click === "function") (el as HTMLElement).click();
  else el.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
}

/** Hover or leave an element as a mouse would: React's enter/leave handlers
 *  listen for over/out, motion's hover for enter/leave on the element itself. */
function hoverElement(el: Element, on: boolean) {
  const Ctor = typeof PointerEvent === "function" ? PointerEvent : MouseEvent;
  const init = { bubbles: true, cancelable: true, pointerType: "mouse", isPrimary: true, relatedTarget: null } as PointerEventInit;
  el.dispatchEvent(new Ctor(on ? "pointerover" : "pointerout", init));
  el.dispatchEvent(new Ctor(on ? "pointerenter" : "pointerleave", { ...init, bubbles: false }));
  el.dispatchEvent(new MouseEvent(on ? "mouseover" : "mouseout", { bubbles: true, relatedTarget: null }));
}

/** Scrolls a target only as far as needed to show it in the room the card
 *  leaves (on a phone, above the docked sheet; below the sticky header),
 *  and not at all when it is already there: the page moves as little as it
 *  can. A target taller than that room starts at its top. */
function bringIntoView(el: HTMLElement, room: { top: number; bottom: number }, smooth: boolean, tallInPanel = false) {
  const HEADER = room.top, reserveBottom = room.bottom;
  const r = el.getBoundingClientRect();
  const roomBottom = window.innerHeight - reserveBottom;
  const fits = r.height <= roomBottom - HEADER;
  if (r.top >= HEADER - 4 && (r.bottom <= roomBottom + 4 || (!fits && r.top < roomBottom - 80))) return;
  el.style.scrollMarginTop = `${HEADER}px`;
  el.style.scrollMarginBottom = `${reserveBottom}px`;
  el.scrollIntoView({ block: fits && !tallInPanel ? "nearest" : "start", inline: "nearest", behavior: smooth ? "smooth" : "auto" });
}

function findTarget(ids: string): HTMLElement | null {
  for (const id of ids.split(",")) {
    const nodes = document.querySelectorAll<HTMLElement>(`[data-tutorial="${id.trim()}"]`);
    for (const node of Array.from(nodes)) {
      const r = node.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return node;
    }
  }
  return null;
}

const dispatchView = (view: TutorialHomeView) =>
  window.dispatchEvent(new CustomEvent<TutorialHomeView>(TUTORIAL_HOME_VIEW_EVENT, { detail: view }));

export default function HowToPlayTutorial() {
  const { settings } = useSettings();
  const reduceMotion = useReducedMotion();
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [replayMode, setReplayMode] = useState(false);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [phase, setPhase] = useState<"press" | "show">("show");
  const [pressing, setPressing] = useState(false);
  const [autoplay, setAutoplay] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [cardSize, setCardSize] = useState({ w: 380, h: 220 });
  const [paused, setPaused] = useState(false);
  const [targetBox, setTargetBox] = useState<TargetBox | null>(null);
  const [touchPointer, setTouchPointer] = useState(false);
  const [readyKey, setReadyKey] = useState<string | null>(null);
  /** Pointer position while a scripted pointer path plays. */
  const [tourPoint, setTourPoint] = useState<{ left: number; top: number } | null>(null);
  const [tourDoneKey, setTourDoneKey] = useState<string | null>(null);
  /** While a pointer path plays, the spotlight follows its current stop. */
  const [tourSpot, setTourSpot] = useState<{ key: string; target: string } | null>(null);
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth < 640);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const cardRef = useRef<HTMLElement>(null);
  const cardHeightRef = useRef(220);
  /** The part of the screen the card leaves free: on a phone, the room
   *  above or below the docked sheet; elsewhere, below the page header. */
  const freeRoom = () => {
    if (window.innerWidth >= 640) return { top: 64, bottom: 12 };
    const docked = placeRef.current;
    return docked && docked.top < window.innerHeight / 2
      ? { top: docked.top + cardHeightRef.current + 12, bottom: 12 }
      : { top: 64, bottom: cardHeightRef.current + 24 };
  };
  /** When the spotlit target last moved or changed size. */
  const targetGrowth = useRef<{ box: TargetBox | null; since: number; start: number }>({ box: null, since: 0, start: 0 });
  const [, setSettleTick] = useState(0);
  /** Where the card was last put (and for which step and sizes), so it can stay there. */
  const placeRef = useRef<(Placement & { vw: number; vh: number; w: number; h: number; key: string }) | null>(null);
  /** Where the pointer was last sent, so the next move can be timed by distance. */
  const lastPointerRef = useRef({ left: 0, top: 0, duration: 0.6 });
  const pressingRef = useRef(false);
  const bypassRef = useRef(false);
  /** What the last press opened, so it can be folded away when the step ends. */
  const openedRef = useRef<{ target: string; opened: string; close?: string } | null>(null);
  /** Continue was pressed before the step had finished appearing: it is
   *  carried out once the step is shown (and its pointer path has played,
   *  fast), so hurrying never leaves a half-drawn step behind. */
  const pendingRef = useRef<number | null>(null);
  const [pendingTick, setPendingTick] = useState(0);

  const scene = WALKTHROUGH_SCENES[sceneIndex];
  /** Identifies the current step and phase; readiness is only ever for this key. */
  const phaseKey = `${sceneIndex}:${phase}`;
  const targetReady = readyKey === phaseKey;
  const tourDone = tourDoneKey === phaseKey;
  const isLast = sceneIndex === WALKTHROUGH_SCENES.length - 1;
  const awaitingPress = phase === "press" && !!scene.press;
  const sceneTarget = (narrow && scene.mobileTarget) || scene.target;
  /** The step's page is on screen. Until then (a page's code still loading,
   *  slow in a development preview) the step waits: no spotlight, no timers. */
  const routeArrived = `${location.pathname}${location.search}` === scene.route;
  const [slowRoute, setSlowRoute] = useState(false);
  /** The spotlight is following a pointer path (not the step's own target). */
  const followingPointer = !awaitingPress && !!tourSpot && tourSpot.key === phaseKey;
  const spotlightId = awaitingPress ? scene.press!.target : followingPointer ? tourSpot!.target : sceneTarget;
  /** The section lit right now (none until it has finished appearing), for the click handler. */
  const targetBoxRef = useRef<TargetBox | null>(null);
  targetBoxRef.current = targetReady ? targetBox : null;
  /** The spotlight as drawn: it glides after a moving target, so a press
   *  is tested against what the player sees as well as the target itself. */
  const spotRef = useRef<HTMLDivElement>(null);
  const inLitSection = useCallback((x: number, y: number) => {
    const box = targetBoxRef.current;
    if (!box) return false;
    if (inHighlight(box, x, y)) return true;
    const drawn = spotRef.current?.getBoundingClientRect();
    return !!drawn && drawn.width > 0 && x >= drawn.left && x <= drawn.right && y >= drawn.top && y <= drawn.bottom;
  }, []);
  /** Until when a click is the echo of a tap already counted as Continue. */
  const tapHandledUntilRef = useRef(0);

  // Note when the target moves or changes size (a panel sliding or growing
  // open, the page scrolling), and look again once it has settled
  // (no further frame may arrive to re-place the card otherwise).
  useEffect(() => {
    const g = targetGrowth.current;
    const b = targetBox;
    if (b && g.box && Math.abs(b.width - g.box.width) < 3 && Math.abs(b.height - g.box.height) < 3 && Math.abs(b.left - g.box.left) < 3 && Math.abs(b.top - g.box.top) < 3) { g.box = b; return; }
    g.box = b;
    const now = performance.now();
    // A new spell of movement after the target had come to rest.
    if (now - g.since > SETTLE_MS) g.start = now;
    g.since = now;
    const t = window.setTimeout(() => setSettleTick((n) => n + 1), SETTLE_MS + 20);
    return () => window.clearTimeout(t);
  }, [targetBox]);

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const query = window.matchMedia("(pointer: coarse)");
    const update = () => setTouchPointer(query.matches || navigator.maxTouchPoints > 0);
    update();
    query.addEventListener?.("change", update);
    return () => query.removeEventListener?.("change", update);
  }, []);

  const foldOpened = useCallback(() => {
    const opened = openedRef.current;
    openedRef.current = null;
    if (!opened || !findTarget(opened.opened)) return;
    const el = findTarget(opened.close ?? opened.target);
    const btn = opened.close ? el : el?.querySelector<HTMLElement>("button") ?? el;
    bypassRef.current = true;
    clickElement(btn);
    bypassRef.current = false;
  }, []);

  const setScene = useCallback((index: number) => {
    const next = Math.max(0, Math.min(index, WALKTHROUGH_SCENES.length - 1));
    foldOpened();
    pendingRef.current = null;
    setSceneIndex(next);
    setCollapsed(false);
    const press = WALKTHROUGH_SCENES[next].press;
    setPhase(press && !press.after ? "press" : "show");
  }, [foldOpened]);

  useEffect(() => {
    const start = (event: Event) => {
      const replay = (event as CustomEvent<{ replay?: boolean }>).detail?.replay === true;
      setReplayMode(replay);
      setScene(0);
      setPaused(false);
      setAutoplay(true);
      setTutorialActive(true);
      setActive(true);
      window.setTimeout(() => {
        void import("@/components/game/ChronicleLayout").catch(() => {});
        void import("@/pages/ChronicleDetail").catch(() => {});
        void import("@/pages/ProtagonistIntro").catch(() => {});
        void import("@/pages/Play").catch(() => {});
      }, 1500);
    };
    window.addEventListener(TUTORIAL_START_EVENT, start);
    return () => window.removeEventListener(TUTORIAL_START_EVENT, start);
  }, [setScene]);

  const finish = useCallback(() => {
    foldOpened();
    window.dispatchEvent(new CustomEvent(TUTORIAL_NEWS_SAMPLE_EVENT, { detail: null }));
    stopWalkthroughCues();
    if (!replayMode) {
      try { localStorage.setItem(TUTORIAL_DONE_KEY, "1"); } catch { /* ignore */ }
    }
    // The last card is the send-off; finishing lands on the map itself.
    try { sessionStorage.setItem("hasEnteredMap", "1"); } catch { /* ignore */ }
    setTutorialActive(false);
    setActive(false);
    navigate("/");
    window.setTimeout(() => window.dispatchEvent(new CustomEvent(TUTORIAL_FINISH_EVENT, { detail: { replay: replayMode } })), 0);
  }, [foldOpened, navigate, replayMode]);

  const goNext = useCallback(() => { if (isLast) finish(); else setScene(sceneIndex + 1); }, [finish, isLast, sceneIndex, setScene]);
  const goPrevious = useCallback(() => setScene(sceneIndex - 1), [sceneIndex, setScene]);

  /** Pointer presses the real control, then its effect happens. */
  const performPress = useCallback(() => {
    const press = scene.press;
    if (!press || pressingRef.current) return;
    pressingRef.current = true;
    setPressing(true);
    playWalkthroughCue("click", settings.masterVolume, settings.musicMuted);
    window.setTimeout(() => {
      pressingRef.current = false;
      setPressing(false);
      if (press.view) dispatchView(press.view);
      if (press.opens) {
        const el = findTarget(press.target);
        const btn = el?.querySelector<HTMLElement>("button") ?? el;
        bypassRef.current = true;
        btn?.click();
        bypassRef.current = false;
        if (press.opened) openedRef.current = { target: press.target, opened: press.opened };
      }
      if (press.next) goNext();
      else setPhase("show");
    }, reduceMotion ? 0 : PRESS_ANIMATION_MS);
  }, [goNext, reduceMotion, scene.press, settings.masterVolume, settings.musicMuted]);

  /** Carry out Continue now: press, move to the press, or go on. */
  const advance = useCallback(() => {
    pendingRef.current = null;
    if (awaitingPress) performPress();
    else if (scene.press?.after) setPhase("press");
    else goNext();
  }, [awaitingPress, goNext, performPress, scene.press]);

  /** The player's Continue skips the rest of this step (its pointer path,
   *  its reading time) and goes straight on. A step that moves on by a
   *  press does that press at once. Only a page that is still loading makes
   *  it wait, and then it goes through as soon as the page is there. */
  const onContinue = useCallback(() => {
    if (!routeArrived) {
      if (pendingRef.current === null) { pendingRef.current = performance.now(); setPendingTick((n) => n + 1); }
      return;
    }
    pendingRef.current = null;
    if (!awaitingPress && scene.press?.after && scene.press.next) performPress();
    else advance();
  }, [advance, awaitingPress, performPress, routeArrived, scene.press]);

  // A Continue pressed while the page was loading goes through once it is there.
  useEffect(() => {
    if (!active || pendingRef.current === null || !routeArrived) return;
    const t = window.setTimeout(advance, reduceMotion ? 0 : 300);
    return () => window.clearTimeout(t);
  }, [active, advance, pendingTick, reduceMotion, routeArrived]);

  // A page that takes long to appear gets a way to ask for it again.
  useEffect(() => {
    setSlowRoute(false);
    if (!active || routeArrived) return;
    const t = window.setTimeout(() => setSlowRoute(true), 8000);
    return () => window.clearTimeout(t);
  }, [active, routeArrived, sceneIndex]);

  // Enter the scene: route, initial home view, target reveal, cue.
  useEffect(() => {
    if (!active) return;
    const current = `${location.pathname}${location.search}`;
    if (current !== scene.route) { navigate(scene.route); return; }
    const timer = window.setTimeout(() => {
      if (scene.homeView) dispatchView(scene.homeView);
      window.dispatchEvent(new CustomEvent(TUTORIAL_TARGET_EVENT, { detail: sceneTarget }));
      window.dispatchEvent(new CustomEvent(TUTORIAL_NEWS_SAMPLE_EVENT, { detail: scene.newsSample ?? null }));
    }, 0);
    titleRef.current?.focus({ preventScroll: true });
    playWalkthroughCue(scene.cue, settings.masterVolume, settings.musicMuted);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, location.pathname, location.search, sceneIndex]);

  // Block the page except the card and the control the player is invited to press.
  useEffect(() => {
    if (!active) return;
    const inCard = (e: Event) => !!cardRef.current && cardRef.current.contains(e.target as Node);
    const hits = (id: string | undefined, e: Event) => {
      const el = id ? findTarget(id) : null;
      return !!el && el.contains(e.target as Node);
    };
    // The control the step will press, whether or not it has asked yet:
    // pressing it yourself does what the guide would have done.
    // Only while that press is still to come: once it has been done (the
    // note opened, the panel shown) pressing the same control again would
    // undo it, so from then on it counts as part of the highlight instead.
    const pressPending = awaitingPress || !!scene.press?.after;
    const onPressTarget = (e: Event) => pressPending && hits(scene.press?.target, e);
    // The control the NEXT step presses to move on (the chronicle in a
    // region's list): pressing it early skips straight past that step.
    const nextPress = WALKTHROUGH_SCENES[sceneIndex + 1]?.press;
    const onNextPressTarget = (e: Event) => !awaitingPress && !!nextPress?.next && !nextPress.opens && hits(nextPress.target, e);
    const realOpen = (e: Event) => !!scene.press?.opens && onPressTarget(e);
    const swallow = (e: Event) => { if (!inCard(e) && !realOpen(e)) e.stopPropagation(); };
    // A press on the highlight moves the tour on; it must not leave a text
    // field it happened to land on focused (a phone would open its keyboard).
    const continueFromHighlight = () => {
      const el = document.activeElement as HTMLElement | null;
      if (el && !cardRef.current?.contains(el) && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) el.blur();
      onContinue();
    };
    const onClick = (e: Event) => {
      if (inCard(e) || bypassRef.current) return;
      // The click that follows a tap already handled below: one tap, one step.
      if (performance.now() < tapHandledUntilRef.current) { e.preventDefault(); e.stopPropagation(); return; }
      if (realOpen(e)) {
        // Let the real control open itself, then the step is done.
        const press = scene.press!;
        playWalkthroughCue("click", settings.masterVolume, settings.musicMuted);
        if (press.opened) openedRef.current = { target: press.target, opened: press.opened };
        pendingRef.current = null;
        window.setTimeout(() => (press.next ? goNext() : setPhase("show")), 0);
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (onPressTarget(e)) { performPress(); return; }
      if (onNextPressTarget(e)) {
        playWalkthroughCue("click", settings.masterVolume, settings.musicMuted);
        setScene(sceneIndex + 2);
        return;
      }
      // A press anywhere in the highlighted section counts as Continue: the
      // whole lit box (its padding, and the gaps between the parts of a
      // highlight that spans several controls), whatever it is lighting at
      // the moment (the step's target, the control the guide is about to
      // press, a stop on the pointer's path, a panel that just opened).
      // While a press is awaited, Continue makes the guide do the press.
      const me = e as MouseEvent;
      const inLight = typeof me.clientX === "number" && inLitSection(me.clientX, me.clientY);
      if (inLight || (!awaitingPress && [sceneTarget, spotlightId].some((t) => !!t && t.split(/[+,]/).some((id) => hits(id, e))))) continueFromHighlight();
    };
    // A finger tap on the highlighted section counts even when the browser
    // makes no click of it: a tap while a panel is still scrolling the target
    // into view only stops the scroll on a phone. A short, still touch inside
    // the lit box is a tap; the click that may follow is then ignored.
    const down = { x: 0, y: 0, t: 0, id: -1 };
    const onDown = (e: Event) => {
      const pe = e as PointerEvent;
      if (pe.pointerType !== "touch" && pe.pointerType !== "pen") return;
      Object.assign(down, { x: pe.clientX, y: pe.clientY, t: performance.now(), id: pe.pointerId });
    };
    const onUp = (e: Event) => {
      const pe = e as PointerEvent;
      if (pe.pointerId !== down.id) return;
      down.id = -1;
      if (inCard(e) || bypassRef.current) return;
      if (Math.hypot(pe.clientX - down.x, pe.clientY - down.y) > 10 || performance.now() - down.t > 600) return;
      if (!inLitSection(pe.clientX, pe.clientY)) return;
      // Controls the step itself reacts to are left to the click handler.
      if (realOpen(e) || onPressTarget(e) || onNextPressTarget(e)) return;
      tapHandledUntilRef.current = performance.now() + 700;
      continueFromHighlight();
      // Focus lands on a field after the pointer is released: clear it once more.
      window.setTimeout(() => {
        const el = document.activeElement as HTMLElement | null;
        if (el && !cardRef.current?.contains(el) && /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) el.blur();
      }, 0);
    };
    const opts = { capture: true };
    window.addEventListener("click", onClick, opts);
    window.addEventListener("pointerdown", onDown, opts);
    window.addEventListener("pointerup", onUp, opts);
    for (const t of ["pointerdown", "mousedown", "touchstart"]) window.addEventListener(t, swallow, opts);
    return () => {
      window.removeEventListener("click", onClick, opts);
      window.removeEventListener("pointerdown", onDown, opts);
      window.removeEventListener("pointerup", onUp, opts);
      for (const t of ["pointerdown", "mousedown", "touchstart"]) window.removeEventListener(t, swallow, opts);
    };
  }, [active, awaitingPress, goNext, inLitSection, onContinue, performPress, scene.press, sceneIndex, sceneTarget, spotlightId, setScene, settings.masterVolume, settings.musicMuted]);


  // Scripted pointer path: travel from stop to stop, linger, and really
  // click where the script says so (the page reacts as if the player did it).
  useEffect(() => {
    if (!active || awaitingPress || !scene.pointerTour || !targetReady) return;
    let cancelled = false;
    const key = phaseKey;
    const timers: number[] = [];
    const wait = (ms: number) => new Promise<void>((resolve) => { timers.push(window.setTimeout(resolve, reduceMotion ? 0 : ms)); });
    let hovered: Element | null = null;
    const unhover = () => { if (hovered) hoverElement(hovered, false); hovered = null; };
    const keepSpot = !!scene.tourKeepsSpot;
    (async () => {
      // Let a panel that has just opened finish settling before the pointer sets off.
      await wait(400);
      if (cancelled) return;
      for (const stop of scene.pointerTour!) {
        const el = findTarget(stop.target);
        if (!el) continue;
        if (!keepSpot) setTourSpot({ key, target: stop.target });
        let r = el.getBoundingClientRect();
        const reserve = freeRoom();
        if (r.top < 0 || r.bottom > window.innerHeight - reserve.bottom) {
          bringIntoView(el, reserve, !reduceMotion);
          await wait(500);
          if (cancelled) return;
          r = el.getBoundingClientRect();
        }
        unhover();
        const point = { left: r.left + r.width * (stop.fx ?? 0.5), top: r.top + r.height * (stop.fy ?? 0.5) };
        setTourPoint(point);
        await wait(POINTER_MOVE_MS);
        if (cancelled) return;
        if (stop.hover) { hoverElement(el, true); hovered = el; }
        if (stop.drag) {
          // Grab and pull: the content moves right-to-left under the pointer.
          // The content travels exactly as far as the hand does, so the
          // timeline stays under the finger (a phone's narrow strip drags
          // a shorter way rather than sliding faster than the hand).
          const from = el.scrollLeft;
          const room = Math.max(0, point.left - r.left - r.width * 0.12);
          const dx = Math.max(0, Math.min(stop.drag, el.scrollWidth - el.clientWidth - from, room));
          const reach = dx;
          if (dx > 4) {
            setPressing(true);
            // A shorter drag takes proportionally less time: same hand speed.
            const ms = reduceMotion ? 0 : Math.round(DRAG_MS * Math.max(0.6, dx / stop.drag));
            const t0 = performance.now();
            await new Promise<void>((resolve) => {
              const tick = () => {
                if (cancelled) { resolve(); return; }
                const p = ms ? Math.min(1, (performance.now() - t0) / ms) : 1;
                const ease = -(Math.cos(Math.PI * p) - 1) / 2;
                el.scrollLeft = from + dx * ease;
                setTourPoint({ left: point.left - reach * ease, top: point.top });
                if (p < 1) requestAnimationFrame(tick); else resolve();
              };
              requestAnimationFrame(tick);
            });
            if (cancelled) return;
            setPressing(false);
          }
        }
        if (stop.scroll) {
          // Scroll the panel slowly, as a reader would.
          const own = el.scrollHeight > el.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(el).overflowY);
          const sc = own ? el : scrollParent(el);
          if (sc) {
            const from = sc.scrollTop;
            const dy = Math.max(0, Math.min(stop.scroll, sc.scrollHeight - sc.clientHeight - from));
            const ms = reduceMotion ? 0 : stop.scrollMs ?? 4000;
            const t0 = performance.now();
            await new Promise<void>((resolve) => {
              const tick = () => {
                if (cancelled) { resolve(); return; }
                const p = ms ? Math.min(1, (performance.now() - t0) / ms) : 1;
                sc.scrollTop = from + dy * (-(Math.cos(Math.PI * p) - 1) / 2);
                if (p < 1) requestAnimationFrame(tick); else resolve();
              };
              requestAnimationFrame(tick);
            });
            if (cancelled) return;
          }
        }
        await wait(stop.pause ?? 500);
        if (cancelled) return;
        if (stop.click) {
          setPressing(true);
          playWalkthroughCue("click", settings.masterVolume, settings.musicMuted);
          await wait(PRESS_ANIMATION_MS);
          if (cancelled) return;
          setPressing(false);
          bypassRef.current = true;
          clickElement(el);
          bypassRef.current = false;
          if (stop.opened) openedRef.current = { target: stop.target, opened: stop.opened, close: stop.close };
        }
      }
      unhover();
      if (cancelled) return;
      // What the path opened (a city's description) becomes the spotlight,
      // and the card steps aside for it rather than for the pointer.
      if (scene.focusAfterTour) {
        await wait(250);
        if (cancelled) return;
        setTourSpot({ key, target: scene.focusAfterTour });
      }
      setTourPoint(null);
      setTourDoneKey(key);
    })();
    return () => {
      cancelled = true;
      timers.forEach((t) => window.clearTimeout(t));
      unhover();
      setTourPoint(null);
      setTourSpot(null);
      setPressing(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, awaitingPress, sceneIndex, targetReady]);

  useLayoutEffect(() => {
    if (!active) return;
    let frame = 0;
    let scrolled = false;
    let nudged = false;
    let ready = false;
    const key = phaseKey;
    const started = performance.now();
    let lostAt: number | null = null;
    /** Clear the spotlight only once the target has stayed away a moment. */
    const lose = () => {
      const now = performance.now();
      if (lostAt === null) lostAt = now;
      if (now - lostAt > TARGET_GRACE_MS) setTargetBox(null);
    };
    const locate = () => {
      if (!routeArrived) {
        lose();
        frame = requestAnimationFrame(locate);
        return;
      }
      if (!spotlightId) {
        // A closing step with nothing to point at: ready at once.
        if (!ready) { ready = true; setReadyKey(key); }
        setTargetBox(null);
        frame = requestAnimationFrame(locate);
        return;
      }
      const element = findTarget(spotlightId.split("+")[0]);
      const rect = findBox(spotlightId);
      const reserve = freeRoom();
      if (element && !rect && !nudged) {
        // Laid out but entirely off screen (below the fold on a phone):
        // bring it into view first, then it is spotlighted as usual.
        nudged = true;
        bringIntoView(element, reserve, !reduceMotion);
      }
      if (element && rect) {
        if (!ready && isFullyShown(element)) { ready = true; setReadyKey(key); }
        if (!scrolled && followingPointer) {
          // The pointer path scrolls its own stops into view; scrolling
          // again for every stop would shift the whole page each time.
          scrolled = true;
        }
        // Scroll once the target has finished appearing (a panel fading or
        // growing in is not yet scrollable), or after a short wait at most.
        if (!scrolled && (ready || performance.now() - started > 700)) {
          // On a phone, a target taller than the room the card leaves is
          // scrolled to the top (clear of the sticky header), so the card
          // covers its tail rather than its beginning.
          // A target taller than the panel it scrolls in (a region's list of
          // chronicles) starts at its top, so its first rows are in view.
          const full = element.getBoundingClientRect();
          const scroller = scrollParent(element);
          const tallInPanel = !!scroller && full.height > scroller.clientHeight;
          // Already showing its first rows (with the panel's own header above
          // them): leave the panel as the player would see it.
          const startsInView = tallInPanel && full.top >= scroller!.getBoundingClientRect().top && full.top < window.innerHeight - 80;
          if (!startsInView) bringIntoView(element, reserve, !reduceMotion, tallInPanel);
          scrolled = true;
        }
        lostAt = null;
        setTargetBox((prev) =>
          prev && prev.left === rect.left && prev.top === rect.top && prev.width === rect.width && prev.height === rect.height
            ? prev : { left: rect.left, top: rect.top, width: rect.width, height: rect.height });
      } else {
        lose();
      }
      frame = requestAnimationFrame(locate);
    };
    frame = requestAnimationFrame(locate);
    return () => cancelAnimationFrame(frame);
  }, [active, location.pathname, routeArrived, spotlightId, reduceMotion, phaseKey, followingPointer]);

  // Autoplay: once the spotlight is visible, press after a beat, or advance
  // after the reading time. A target that never appears is waited for only
  // TARGET_WAIT_MS, so the tour cannot stall.
  useEffect(() => {
    if (!active || !autoplay || paused) return;
    if (!targetReady) {
      if (!routeArrived) return;
      const wait = window.setTimeout(() => setReadyKey(phaseKey), TARGET_WAIT_MS);
      return () => window.clearTimeout(wait);
    }
    // A scripted pointer path plays out first; the reader has been reading meanwhile.
    if (scene.pointerTour && !awaitingPress && !tourDone) return;
    const reading = scene.pointerTour
      ? scene.afterTourMs ?? Math.max(3000, sceneDuration(scene) - pointerTourMs(scene))
      : scene.durationMs ?? sceneDuration(scene);
    const timeout = window.setTimeout(advance, awaitingPress ? AUTO_PRESS_MS : reading);
    return () => window.clearTimeout(timeout);
  }, [active, advance, autoplay, awaitingPress, paused, phaseKey, routeArrived, scene, targetReady, tourDone]);

  useLayoutEffect(() => {
    if (!active || !cardRef.current) return;
    const el = cardRef.current;
    const measure = () => {
      if (el.offsetHeight) cardHeightRef.current = el.offsetHeight;
      setCardSize((p) => (p.w === el.offsetWidth && p.h === el.offsetHeight) || !el.offsetWidth ? p : { w: el.offsetWidth, h: el.offsetHeight });
    };
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    measure();
    return () => ro?.disconnect();
  }, [active, sceneIndex, collapsed, phase]);

  useEffect(() => {
    if (!active) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowRight") onContinue();
      if (event.key === "ArrowLeft") goPrevious();
      if (event.key === "Escape") finish();
      if (event.key === " " && autoplay) { event.preventDefault(); setPaused((value) => !value); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [active, autoplay, finish, goPrevious, onContinue]);

  if (!active) return null;

  const clampX = (x: number) => Math.max(24, Math.min(window.innerWidth - 24, x));
  const clampY = (y: number) => Math.max(24, Math.min(window.innerHeight - 24, y));
  // A press goes straight for the middle of the control. While a step is
  // explained, the pointer rests beside what is being explained, low and to
  // the right as a hand would, instead of sitting on top of it.
  const anchor = awaitingPress && targetBox
    ? { left: targetBox.left + targetBox.width / 2, top: targetBox.top + targetBox.height / 2 }
    : tourPoint ?? (targetBox ? {
      left: targetBox.left + targetBox.width * (targetBox.width > 600 ? 0.72 : 0.86),
      top: targetBox.top + targetBox.height * (targetBox.height > 400 ? 0.74 : 0.88),
    } : { left: 0, top: 0 });
  const pointerLeft = clampX(anchor.left);
  const pointerTop = clampY(anchor.top);
  // Resting, it sways a little, the way a hand never holds quite still.
  const pointerGone = !!scene.fadePointerAfterTour && tourDone && !awaitingPress;
  const drifting = !!targetBox && !awaitingPress && !tourPoint && !reduceMotion && !pointerGone;
  const drift = !drifting
    ? { x: 0, y: 0 }
    : touchPointer
      ? { x: 0, y: [0, -4, 0] }
      : { x: [0, 7, 2, -5, 0], y: [0, 4, 9, 4, 0] };
  // It glides, eased at both ends, taking longer for longer distances; small
  // steps (a drag, a scroll keeping up) follow at once.
  // Timed once per new destination (a repeated render reuses it).
  const lastPointer = lastPointerRef.current;
  if (lastPointer.left !== pointerLeft || lastPointer.top !== pointerTop) {
    const travel = Math.hypot(pointerLeft - lastPointer.left, pointerTop - lastPointer.top);
    lastPointerRef.current = { left: pointerLeft, top: pointerTop, duration: travel < 30 ? 0.12 : Math.min(1.15, 0.4 + travel / 1400) };
  }
  const pointerMove = { type: "tween" as const, ease: [0.33, 0, 0.2, 1] as [number, number, number, number], duration: lastPointerRef.current.duration };

  const progress = Math.round(((sceneIndex + (phase === "show" ? 1 : 0.5)) / WALKTHROUGH_SCENES.length) * 100);
  const vw = window.innerWidth, vh = window.innerHeight;
  // A short screen (a phone held sideways): the card keeps to a little over
  // half the height, its text scrolls inside, and Continue stays in reach,
  // leaving room to see (and press) the highlight.
  const short = vh < 480;
  const cardMaxH = short ? Math.max(150, Math.round(vh * 0.55)) : vh - 24;
  const mobile = vw < 640;
  // While a pointer path plays, the spotlight follows the pointer but the
  // card holds still, so the page does not jump about: on a phone it docks
  // at the bottom, on a desktop it is placed around the whole step (both
  // house cards, say). A closing step with nothing to point at is centred.
  // A path that ends on one of its own stops (the chosen house) keeps the
  // card where it was for the rest of the step as well.
  const tourRunning = !!scene.pointerTour && !awaitingPress && followingPointer && (!tourDone || !scene.focusAfterTour);
  const avoid = (tourRunning && findBox(scene.cardAround ?? sceneTarget)) || targetBox;
  // The card holds its place while it still clears what it explains and
  // stays on screen: a page scrolling or a panel growing in does not drag
  // it along. When it must move, it keeps the side it was on if that fits.
  // A new step's target is waited for where the card already is (not in a
  // corner), and on a wide screen the card only stays put for a new step if
  // it is still close to the new target.
  const held = placeRef.current;
  const near = (b: TargetBox, p: { left: number; top: number }) =>
    Math.max(0, b.left - (p.left + cardSize.w), p.left - (b.left + b.width)) + Math.max(0, b.top - (p.top + cardSize.h), p.top - (b.top + b.height)) < 160;
  // (On a phone the sheet keeps to its edge as the card grows or shrinks.)
  const heldAt = held && mobile && held.side === "dock" && held.top > vh / 2 ? { ...held, top: vh - cardSize.h - 12 } : held;
  const onScreen = !!heldAt && heldAt.vw === vw && heldAt.vh === vh && heldAt.w === cardSize.w
    && heldAt.left >= 0 && heldAt.top >= 0 && heldAt.left + cardSize.w <= vw && heldAt.top + cardSize.h <= vh;
  // A panel still growing open (or a page still scrolling) would chase the
  // card from place to place: the card waits where it is until the target
  // has come to rest, then moves once.
  // A target that never quite stops moving (a list nudged every frame) is
  // waited for at most MAX_HOLD_MS, so the card cannot stay on top of it.
  const growing = !!avoid && !!targetGrowth.current.box && performance.now() - targetGrowth.current.since < SETTLE_MS
    && performance.now() - targetGrowth.current.start < MAX_HOLD_MS;
  const stillGood = onScreen && (!avoid || growing
    || (!overlapsBox(avoid, heldAt!, cardSize.w, cardSize.h) && (mobile || heldAt!.key === phaseKey || near(avoid, heldAt!))));
  const place: Placement = !scene.target && !awaitingPress
    ? { left: Math.max(12, (vw - cardSize.w) / 2), top: Math.max(12, (vh - cardSize.h) / 2), side: "dock" }
    : stillGood
      ? heldAt!
      : placeCard(avoid, cardSize.w, cardSize.h, vw, vh, mobile, mobile ? (held && held.top < vh / 2 ? "top" : "bottom") : held?.side);
  placeRef.current = { ...place, vw, vh, w: cardSize.w, h: cardSize.h, key: phaseKey };
  const minor = scene.weight !== "major" && !awaitingPress;
  const canCollapse = (minor || mobile) && !!scene.target;
  const arrow = targetBox && place.side !== "dock" ? (() => {
    const cx = targetBox.left + targetBox.width / 2, cy = targetBox.top + targetBox.height / 2;
    const ay = Math.max(12, Math.min(cardSize.h - 24, cy - place.top - 6));
    const ax = Math.max(12, Math.min(cardSize.w - 24, cx - place.left - 6));
    if (place.side === "right") return { left: -7, top: ay, rot: 45 };
    if (place.side === "left") return { left: cardSize.w - 7, top: ay, rot: 225 };
    if (place.side === "bottom") return { left: ax, top: -7, rot: 135 };
    return { left: ax, top: cardSize.h - 7, rot: 315 };
  })() : null;

  return createPortal(
    <AnimatePresence>
      <motion.div className="fixed inset-0 z-[11000] pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} role="dialog" aria-modal="true" aria-labelledby="walkthrough-title">
        {/* Until its target has finished appearing (a page fading in), the
            spotlight waits and the page is simply dimmed, so it never frames
            an empty box. */}
        {/* One dimming strength on every step and screen size, so moving
            from step to step (or from the plain dim to the spotlight's)
            never flickers the page darker and lighter. */}
        <motion.div
          className="absolute inset-0 bg-background/50"
          initial={false}
          animate={{ opacity: !targetBox || !targetReady ? 1 : 0 }}
          transition={{ duration: reduceMotion ? 0 : 0.25 }}
        />
        {targetBox && (
          <>
            <motion.div
              ref={spotRef}
              className={`fixed rounded-sm border-2 shadow-[0_0_0_9999px_hsl(var(--background)/0.5),0_0_18px_hsl(var(--primary)/0.45)] ${minor ? "border-primary/75" : "border-primary"}`}
              initial={false}
              animate={{ left: targetBox.left - SPOTLIGHT_PAD, top: targetBox.top - SPOTLIGHT_PAD, width: targetBox.width + SPOTLIGHT_PAD * 2, height: targetBox.height + SPOTLIGHT_PAD * 2, opacity: targetReady ? 1 : 0 }}
              transition={reduceMotion ? { duration: 0 } : { ...GLIDE, opacity: { duration: 0.25 } }}
            />
            <motion.div
              className="fixed z-10 -ml-5 -mt-5 flex h-10 w-10 items-center justify-center"
              initial={false}
              animate={{ left: pointerLeft, top: pointerTop, opacity: pointerGone ? 0 : 1 }}
              transition={reduceMotion ? { duration: 0 } : { ...pointerMove, opacity: { duration: 0.7, ease: "easeOut" } }}
              aria-hidden="true"
            >
              <motion.div
                className="relative flex h-10 w-10 items-center justify-center"
                animate={drift}
                transition={drifting ? { duration: touchPointer ? 2 : 8, repeat: Infinity, ease: "easeInOut" } : { duration: 0.4 }}
              >
              {pressing && (
                <motion.span
                  className="absolute inset-0 rounded-full border-2 border-primary"
                  initial={{ scale: 0.4, opacity: 0.9 }}
                  animate={{ scale: touchPointer ? 2.2 : 1.6, opacity: 0 }}
                  transition={{ duration: PRESS_ANIMATION_MS / 1000 }}
                />
              )}
              <motion.span
                className="relative flex h-10 w-10 items-center justify-center rounded-full border border-primary/60 bg-card text-primary shadow-lg"
                animate={{ scale: pressing ? 0.7 : awaitingPress && !reduceMotion ? [1, 0.88, 1] : 1, rotate: pressing ? -8 : 0 }}
                transition={pressing ? { duration: 0.14 } : { duration: 1.1, repeat: awaitingPress ? Infinity : 0 }}
              >
                {touchPointer ? <Hand className="h-5 w-5" /> : <MousePointer2 className="h-5 w-5" />}
              </motion.span>
              </motion.div>
            </motion.div>
          </>
        )}

        <motion.section
          ref={cardRef}
          data-walkthrough-card=""
          className={`pointer-events-auto fixed flex flex-col rounded-sm border border-primary/30 atlas-surface-elevated shadow-2xl ${mobile ? "w-[calc(100vw-24px)]" : "w-[360px]"} ${collapsed ? "p-2" : mobile || short ? "p-3" : "p-4"}`}
          style={{ maxHeight: cardMaxH }}
          initial={false}
          animate={{ left: place.left, top: place.top }}
          transition={reduceMotion ? { duration: 0 } : GLIDE}
        >
          {arrow && <span aria-hidden="true" className="absolute h-3 w-3 border-b border-l border-primary/30 bg-card" style={{ left: arrow.left, top: arrow.top, transform: `rotate(${arrow.rot}deg)` }} />}
          {collapsed ? (
            <button onClick={() => setCollapsed(false)} className="flex w-full items-center gap-2 text-left" aria-label={`Expand explanation: ${scene.title}`}>
              <scene.Icon className="h-4 w-4 shrink-0 text-primary" />
              <span className="flex-1 truncate font-display text-xs text-foreground">{scene.title}</span>
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            </button>
          ) : (<>
          <div className="flex min-h-0 items-start gap-3 overflow-y-auto overscroll-contain">
            <div className={`flex shrink-0 items-center justify-center rounded-sm border border-primary/30 bg-primary/10 text-primary ${mobile ? "h-8 w-8" : "h-9 w-9"}`}><scene.Icon className="h-4 w-4" /></div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-display text-[9px] uppercase tracking-[0.28em] text-gold-dim">{scene.eyebrow}</p>
                  <h2 id="walkthrough-title" ref={titleRef} tabIndex={-1} className={`mt-1 font-display leading-snug text-foreground outline-none text-base`}>{scene.title}</h2>
                </div>
                <div className="flex gap-1">
                  {autoplay && <Button variant="ghost" size="icon" onClick={() => setPaused((value) => !value)} aria-label={paused ? "Resume walkthrough" : "Pause walkthrough"} className="h-9 w-9 rounded-sm">{paused ? <Play /> : <Pause />}</Button>}
                  {canCollapse && <Button variant="ghost" size="icon" onClick={() => setCollapsed(true)} aria-label="Collapse explanation" className="h-9 w-9 rounded-sm"><ChevronUp /></Button>}
                  <Button variant="ghost" size="icon" onClick={finish} aria-label="Exit walkthrough" title="Skip the tour" className="h-9 w-9 rounded-sm"><X /></Button>
                </div>
              </div>
              {!routeArrived && (
                <p className="mt-2 font-body text-xs text-muted-foreground" aria-live="polite">
                  <span className="animate-pulse">Opening the page…</span>
                  {slowRoute && (<> It is taking a while.{" "}
                    <button type="button" onClick={() => navigate(scene.route)} className="underline underline-offset-2 hover:text-foreground">Try again</button>
                  </>)}
                </p>
              )}
              {awaitingPress
                ? <p className="mt-2 font-body text-sm leading-relaxed text-primary">{touchPointer ? scene.press!.prompt.replace(/^Press/, "Tap") : scene.press!.prompt} <span className="text-muted-foreground">Or press Continue and the guide will do it.</span></p>
                : <p className={`mt-2 font-body text-muted-foreground ${mobile ? "text-[13px] leading-snug" : "text-sm leading-relaxed"}`}>{scene.body}{targetBox && targetReady && <span className="mt-1 block font-display text-[9px] uppercase tracking-[0.18em] text-primary">{touchPointer ? "Tap" : "Click"} the highlighted area to continue</span>}</p>}
              {sceneIndex === 0 && !awaitingPress && (
                <p className="mt-2 font-body text-xs text-muted-foreground/80">
                  The tour plays itself and takes a few minutes.{" "}
                  <button type="button" onClick={finish} className="underline underline-offset-2 hover:text-foreground">Skip it</button>
                  {" "}and find it again any time in Settings.
                </p>
              )}
            </div>
          </div>
          <div className={`${mobile || short ? "mt-2" : "mt-4"} h-1 shrink-0 overflow-hidden rounded-full bg-muted`} role="progressbar" aria-label="Tour progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress}>
            <motion.div className="h-full rounded-full bg-primary" initial={false} animate={{ width: `${progress}%` }} transition={{ duration: reduceMotion ? 0 : 0.6, ease: "easeOut" }} />
          </div>
          <div className={`${mobile || short ? "mt-2" : "mt-3"} flex shrink-0 items-center gap-2`}>
            <Button variant="outline" size="icon" onClick={goPrevious} disabled={sceneIndex === 0} aria-label="Previous walkthrough scene" className={`${mobile ? "h-10 w-10" : "h-11 w-11"} rounded-sm`}><ChevronLeft /></Button>
            <Button onClick={onContinue} className={`${mobile ? "h-10" : "h-11"} flex-1 rounded-sm font-display text-[10px] uppercase tracking-[0.16em]`}>{isLast && !awaitingPress ? "Finish" : "Continue"}{!(isLast && !awaitingPress) && <ChevronRight />}</Button>
            <label className="flex shrink-0 items-center gap-1.5 font-display text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
              <Switch checked={autoplay} onCheckedChange={(v) => { setAutoplay(v); setPaused(false); }} aria-label="Autoplay walkthrough" />
              Auto
            </label>
          </div>
          </>)}
          <p className="sr-only" aria-live="polite">Tour: {scene.title}</p>
        </motion.section>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
